import type { GlossaryEntry } from "./glossary";

/**
 * Apply glossary anchor ids to the FAQ-000 dictionary page.
 * For each paragraph whose first <strong> child holds a term, assign id=slug,
 * mark the node so the copy-link + flash-on-hash behavior can find it,
 * and inject a small "copy anchor link" icon that appears on hover.
 */
export function applyDictionaryAnchors(root: HTMLElement, entries: GlossaryEntry[]) {
  const bySlug = new Map(entries.map((e) => [e.slug, e]));
  const paragraphs = root.querySelectorAll("p");
  paragraphs.forEach((p) => {
    const strong = p.querySelector(":scope > strong, :scope > b");
    if (!strong) return;
    const raw = (strong.textContent || "").trim().replace(/\.+$/, "");
    if (!raw) return;
    // Slug via the same rule as parseDictionary; we look it up in the entry set to be safe.
    const slugGuess = slugifyForLookup(raw);
    const entry = bySlug.get(slugGuess);
    if (!entry) return;
    p.setAttribute("id", entry.slug);
    p.classList.add("glossary-entry");
    if (!p.querySelector(".glossary-anchor-btn")) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "glossary-anchor-btn";
      btn.setAttribute("aria-label", `Copy link to ${entry.term}`);
      btn.title = "Copy link";
      btn.textContent = "🔗";
      btn.addEventListener("click", (ev) => {
        ev.preventDefault();
        const url = `${window.location.origin}/support/library/dictionary#${entry.slug}`;
        navigator.clipboard?.writeText(url).catch(() => {});
        btn.classList.add("copied");
        window.setTimeout(() => btn.classList.remove("copied"), 900);
      });
      p.appendChild(btn);
    }
  });
}

function slugifyForLookup(raw: string): string {
  let src = raw.trim().replace(/\.+$/, "");
  const parenIdx = src.indexOf("(");
  if (parenIdx > 0) {
    const close = src.indexOf(")", parenIdx);
    const inner = close > 0 ? src.slice(parenIdx + 1, close) : "";
    if (/\s/.test(inner) || /[A-Za-z]{3,}/.test(inner)) src = src.slice(0, parenIdx).trim();
  }
  return src.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

/**
 * Scroll to and briefly highlight the entry matching the current hash.
 * Call after applyDictionaryAnchors and after paint.
 */
export function flashHashEntry(root: HTMLElement) {
  const hash = decodeURIComponent(window.location.hash || "").replace(/^#/, "");
  if (!hash) return;
  const el = root.querySelector(`#${cssEscape(hash)}`) as HTMLElement | null;
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.classList.remove("glossary-flash");
  // reflow to restart animation
  void el.offsetWidth;
  el.classList.add("glossary-flash");
}

function cssEscape(s: string): string {
  return (window as any).CSS?.escape ? (window as any).CSS.escape(s) : s.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}

// ---------- Auto-link glossary terms in other docs ----------

const SKIP_TAGS = new Set(["A", "CODE", "PRE", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6"]);

/**
 * Dictionary terms whose primary form is also an everyday English word.
 * For these, we refuse to link bare lowercase occurrences (which produce
 * false positives like "will open an account" or "the person"). We still
 * link when the occurrence is capitalized mid-sentence ("your Will") or
 * when it appears as a distinctive multi-word alias ("the Record",
 * "minute book"). Multi-word aliases bypass this rule entirely.
 */
const COMMON_WORD_STOPLIST = new Set([
  "will",
  "trust",
  "estate",
  "title",
  "deed",
  "seal",
  "adopt",
  "adoption",
  "record",
  "records",
  "minute",
  "minutes",
  "journal",
  "distribution",
  "engine",
  "vault",
]);

export function autoLinkGlossary(root: HTMLElement, entries: GlossaryEntry[]) {
  if (!entries.length) return;

  type Item = {
    entry: GlossaryEntry;
    alias: string;
    aliasLower: string;
    isMultiWord: boolean;
    isCommonWord: boolean;
  };
  const items: Item[] = [];
  for (const e of entries) {
    for (const a of e.aliases) {
      const al = a.trim();
      if (al.length < 2) continue;
      const aliasLower = al.toLowerCase();
      const isMultiWord = /\s/.test(aliasLower);
      const isCommonWord = !isMultiWord && COMMON_WORD_STOPLIST.has(aliasLower);
      items.push({ entry: e, alias: al, aliasLower, isMultiWord, isCommonWord });
    }
  }
  items.sort((a, b) => b.aliasLower.length - a.aliasLower.length);

  const linkedSlugs = new Set<string>();

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const t = node.parentElement;
      if (!t) return NodeFilter.FILTER_REJECT;
      let cur: HTMLElement | null = t;
      while (cur && cur !== root) {
        if (SKIP_TAGS.has(cur.tagName)) return NodeFilter.FILTER_REJECT;
        if (cur.classList && cur.classList.contains("glossary-link")) return NodeFilter.FILTER_REJECT;
        cur = cur.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  } as any);

  const textNodes: Text[] = [];
  let n: Node | null = walker.nextNode();
  while (n) { textNodes.push(n as Text); n = walker.nextNode(); }

  for (const tn of textNodes) {
    const original = tn.nodeValue || "";
    if (!original.trim()) continue;

    let remaining = original;
    let cursorNode: Text = tn;
    let didSplit = false;

    while (true) {
      let best: { idx: number; len: number; item: Item } | null = null;
      const lower = remaining.toLowerCase();
      for (const it of items) {
        if (linkedSlugs.has(it.entry.slug)) continue;
        // "Wealthy Steward" is the organization name, never a glossary hit.
        const reject = it.aliasLower.startsWith("steward")
          ? (hay: string, at: number) => isPrecededByWealthy(hay, at)
          : undefined;
        const idx = it.isCommonWord
          ? findCapitalizedMidSentence(remaining, it.aliasLower, reject)
          : findWord(lower, it.aliasLower, reject);
        if (idx === -1) continue;
        if (!best || idx < best.idx || (idx === best.idx && it.aliasLower.length > best.len)) {
          best = { idx, len: it.aliasLower.length, item: it };
        }
      }
      if (!best) break;

      const before = remaining.slice(0, best.idx);
      const matched = remaining.slice(best.idx, best.idx + best.len);
      const after = remaining.slice(best.idx + best.len);

      const parent = cursorNode.parentNode;
      if (!parent) break;

      const anchor = document.createElement("a");
      anchor.className = "glossary-link";
      anchor.href = `/support/library/dictionary#${best.item.entry.slug}`;
      anchor.setAttribute("data-glossary-slug", best.item.entry.slug);
      anchor.setAttribute("data-glossary-term", best.item.entry.term);
      anchor.setAttribute("data-glossary-def", best.item.entry.definition);
      anchor.textContent = matched;

      const beforeNode = document.createTextNode(before);
      const afterNode = document.createTextNode(after);

      parent.insertBefore(beforeNode, cursorNode);
      parent.insertBefore(anchor, cursorNode);
      parent.insertBefore(afterNode, cursorNode);
      parent.removeChild(cursorNode);

      linkedSlugs.add(best.item.entry.slug);
      didSplit = true;

      cursorNode = afterNode;
      remaining = after;
      if (!remaining) break;
    }

    if (!didSplit) continue;
  }

  attachTooltipDelegation(root);
}

type RejectFn = (hay: string, at: number) => boolean;

/**
 * True when the occurrence at `at` is immediately preceded by the word
 * "wealthy" (case-insensitive, whitespace only in between). Used to keep the
 * organization name "Wealthy Steward" out of the glossary linker.
 */
function isPrecededByWealthy(hay: string, at: number): boolean {
  let j = at - 1;
  while (j >= 0 && /\s/.test(hay[j])) j--;
  if (j === at - 1) return false; // no whitespace separator
  const end = j + 1;
  while (j >= 0 && isWordChar(hay[j])) j--;
  const word = hay.slice(j + 1, end);
  return word.toLowerCase() === "wealthy";
}

function findWord(hay: string, needle: string, reject?: RejectFn): number {
  if (!needle) return -1;
  let from = 0;
  while (from <= hay.length - needle.length) {
    const idx = hay.indexOf(needle, from);
    if (idx === -1) return -1;
    const prev = idx === 0 ? " " : hay[idx - 1];
    const next = hay[idx + needle.length] ?? " ";
    if (!isWordChar(prev) && !isWordChar(next) && !(reject && reject(hay, idx))) return idx;
    from = idx + 1;
  }
  return -1;
}

/**
 * Case-sensitive-ish search for stoplisted common-word aliases: only accept
 * the match if the first letter of the actual occurrence is uppercase AND the
 * occurrence is not at the start of a sentence (previous non-whitespace char
 * exists and is not `.`, `!`, or `?`). Word boundaries required on both sides.
 */
function findCapitalizedMidSentence(hay: string, needleLower: string, reject?: RejectFn): number {
  if (!needleLower) return -1;
  const hayLower = hay.toLowerCase();
  const needleLen = needleLower.length;
  let from = 0;
  while (from <= hay.length - needleLen) {
    const idx = hayLower.indexOf(needleLower, from);
    if (idx === -1) return -1;
    const first = hay[idx];
    const prev = idx === 0 ? " " : hay[idx - 1];
    const next = hay[idx + needleLen] ?? " ";
    const wordBoundary = !isWordChar(prev) && !isWordChar(next);
    const isUpper = first >= "A" && first <= "Z";
    if (wordBoundary && isUpper && !(reject && reject(hay, idx))) {
      // Walk backwards over whitespace to find the previous non-whitespace char.
      let j = idx - 1;
      while (j >= 0 && /\s/.test(hay[j])) j--;
      const prevNonWs = j >= 0 ? hay[j] : "";
      // Require prior context to be present and not a sentence terminator.
      if (prevNonWs && !/[.!?]/.test(prevNonWs)) {
        return idx;
      }
    }
    from = idx + 1;
  }
  return -1;
}

function isWordChar(ch: string): boolean {
  return /[A-Za-z0-9_]/.test(ch);
}

// ---------- Tooltip (single delegated instance per root) ----------

const TOOLTIP_ATTR = "data-glossary-tooltip-bound";

/** Per-root teardown handles so a remount never stacks listeners or tooltips. */
const tooltipTeardowns = new WeakMap<HTMLElement, () => void>();

/**
 * Remove any tooltip this root created and unbind its listeners. Safe to call
 * on a root that was never bound.
 */
export function detachTooltipDelegation(root: HTMLElement) {
  const teardown = tooltipTeardowns.get(root);
  if (teardown) {
    teardown();
    tooltipTeardowns.delete(root);
  }
  root.removeAttribute(TOOLTIP_ATTR);
  document.querySelectorAll(".glossary-tooltip").forEach((n) => n.remove());
}

function attachTooltipDelegation(root: HTMLElement) {
  if (root.getAttribute(TOOLTIP_ATTR) === "1") return;
  root.setAttribute(TOOLTIP_ATTR, "1");

  const ac = new AbortController();
  const { signal } = ac;

  let tip: HTMLDivElement | null = null;

  let hideTimer: number | null = null;
  let longPressTimer: number | null = null;

  const show = (link: HTMLAnchorElement) => {
    hide(true);
    const def = link.getAttribute("data-glossary-def") || "";
    const slug = link.getAttribute("data-glossary-slug") || "";
    const term = link.getAttribute("data-glossary-term") || "";
    tip = document.createElement("div");
    tip.className = "glossary-tooltip";
    tip.innerHTML = `
      <div class="glossary-tooltip-term"></div>
      <div class="glossary-tooltip-def"></div>
      <a class="glossary-tooltip-more" href="/support/library/dictionary#${slug}">Full dictionary →</a>
    `;
    (tip.querySelector(".glossary-tooltip-term") as HTMLElement).textContent = term;
    (tip.querySelector(".glossary-tooltip-def") as HTMLElement).textContent = def.slice(0, 320) + (def.length > 320 ? "…" : "");
    document.body.appendChild(tip);
    const r = link.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    const pad = 8;
    let left = r.left + window.scrollX;
    if (left + tw + pad > window.scrollX + window.innerWidth) {
      left = window.scrollX + window.innerWidth - tw - pad;
    }
    let top = r.bottom + window.scrollY + 6;
    if (top + th + pad > window.scrollY + window.innerHeight) {
      top = r.top + window.scrollY - th - 6;
    }
    tip.style.left = `${Math.max(pad, left)}px`;
    tip.style.top = `${Math.max(pad, top)}px`;
  };

  const hide = (immediate = false) => {
    if (hideTimer) { window.clearTimeout(hideTimer); hideTimer = null; }
    const doHide = () => { if (tip) { tip.remove(); tip = null; } };
    if (immediate) doHide(); else hideTimer = window.setTimeout(doHide, 120);
  };

  root.addEventListener("mouseover", (e) => {
    const target = (e.target as HTMLElement).closest("a.glossary-link") as HTMLAnchorElement | null;
    if (target) show(target);
  }, { signal });
  root.addEventListener("mouseout", (e) => {
    const target = (e.target as HTMLElement).closest("a.glossary-link");
    if (target) hide();
  }, { signal });
  root.addEventListener("touchstart", (e) => {
    const target = (e.target as HTMLElement).closest("a.glossary-link") as HTMLAnchorElement | null;
    if (!target) return;
    if (longPressTimer) window.clearTimeout(longPressTimer);
    longPressTimer = window.setTimeout(() => show(target), 400);
  }, { passive: true, signal });
  root.addEventListener("touchend", () => {
    if (longPressTimer) { window.clearTimeout(longPressTimer); longPressTimer = null; }
  }, { signal });
  window.addEventListener("scroll", () => hide(true), { passive: true, signal });

  tooltipTeardowns.set(root, () => {
    ac.abort();
    if (hideTimer) { window.clearTimeout(hideTimer); hideTimer = null; }
    if (longPressTimer) { window.clearTimeout(longPressTimer); longPressTimer = null; }
    hide(true);
  });
}

